import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "hotlead_approved_contacts_v1";

const defaultState = {
  contacts: {},
};

const ApprovedContactsContext = createContext(null);

const readStorage = () => {
  if (typeof window === "undefined") {
    return { ...defaultState };
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...defaultState };
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") {
      if (Array.isArray(parsed.contacts)) {
        const mapped = {};
        parsed.contacts.forEach((contact) => {
          if (contact?.id) {
            mapped[contact.id] = contact;
          }
        });
        return { contacts: mapped };
      }
      if (parsed.contacts && typeof parsed.contacts === "object") {
        return {
          contacts: parsed.contacts,
        };
      }
    }
  } catch (error) {
    console.warn("Failed to load approved contacts from storage", error);
  }

  return { ...defaultState };
};

export const ApprovedContactsProvider = ({ children }) => {
  const [contactsById, setContactsById] = useState(() => readStorage().contacts);

  // Persist to localStorage + broadcast updates
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const payload = JSON.stringify({ contacts: contactsById });
      window.localStorage.setItem(STORAGE_KEY, payload);
      window.dispatchEvent(
        new CustomEvent("approvedContactsUpdated", {
          detail: { total: Object.keys(contactsById).length },
        })
      );
    } catch (error) {
      console.warn("Unable to persist approved contacts", error);
    }
  }, [contactsById]);

  // Sync when storage is updated from other tabs
  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key !== STORAGE_KEY || !event.newValue) return;
      try {
        const parsed = JSON.parse(event.newValue);
        if (parsed?.contacts) {
          setContactsById(parsed.contacts);
        }
      } catch (error) {
        console.warn("Unable to parse approved contacts storage event", error);
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const contacts = useMemo(() => {
    return Object.values(contactsById || {}).sort((a, b) => {
      const aTime = new Date(a.approvedAt || a.updatedAt || 0).getTime();
      const bTime = new Date(b.approvedAt || b.updatedAt || 0).getTime();
      return bTime - aTime;
    });
  }, [contactsById]);

  const stats = useMemo(() => {
    const summary = {
      total: contacts.length,
      byStatus: {
        pending: 0,
        enriching: 0,
        ready: 0,
        failed: 0,
      },
    };
    contacts.forEach((contact) => {
      const status = contact.status || "pending";
      if (!summary.byStatus[status]) {
        summary.byStatus[status] = 0;
      }
      summary.byStatus[status] += 1;
    });
    return summary;
  }, [contacts]);

  const addApprovedContacts = useCallback((newContacts = []) => {
    if (!Array.isArray(newContacts) || newContacts.length === 0) return;
    setContactsById((prev) => {
      const next = { ...prev };
      newContacts.forEach((contact) => {
        if (!contact?.id) return;
        const existing = next[contact.id] || {};
        next[contact.id] = {
          ...existing,
          ...contact,
          approvedAt: contact.approvedAt || existing.approvedAt || new Date().toISOString(),
          status: contact.status || existing.status || "pending",
          updatedAt: new Date().toISOString(),
        };
      });
      return next;
    });
  }, []);

  const updateApprovedContact = useCallback((contactId, updates = {}) => {
    if (!contactId || !updates) return;
    setContactsById((prev) => {
      if (!prev[contactId]) return prev;
      return {
        ...prev,
        [contactId]: {
          ...prev[contactId],
          ...updates,
          updatedAt: new Date().toISOString(),
        },
      };
    });
  }, []);

  const removeApprovedContacts = useCallback((contactIds = []) => {
    if (!Array.isArray(contactIds) || contactIds.length === 0) return;
    setContactsById((prev) => {
      const next = { ...prev };
      contactIds.forEach((id) => {
        if (id && next[id]) {
          delete next[id];
        }
      });
      return next;
    });
  }, []);

  const clearApprovedContacts = useCallback(() => {
    setContactsById({});
  }, []);

  const contextValue = useMemo(
    () => ({
      contacts,
      contactsById,
      stats,
      addApprovedContacts,
      updateApprovedContact,
      removeApprovedContacts,
      clearApprovedContacts,
    }),
    [
      contacts,
      contactsById,
      stats,
      addApprovedContacts,
      updateApprovedContact,
      removeApprovedContacts,
      clearApprovedContacts,
    ]
  );

  return (
    <ApprovedContactsContext.Provider value={contextValue}>
      {children}
    </ApprovedContactsContext.Provider>
  );
};

export const useApprovedContacts = () => {
  const context = useContext(ApprovedContactsContext);
  if (!context) {
    throw new Error("useApprovedContacts must be used within ApprovedContactsProvider");
  }
  return context;
};
